import { useMemo, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import clsx from 'clsx';
import type { ChartData, ChartOptions, Plugin } from 'chart.js';
import {
  CHART_COLORS,
  getDoughnutChartOptions,
  resolveDatasetColor,
} from './chartConfig';
import { createChartRenderCompletePlugin } from './createChartRenderCompletePlugin';
import type { DoughnutChartProps } from './types';

function shouldShowDoughnutPlaceholder(
  labels: string[],
  data: number[],
): boolean {
  if (labels.length === 0 || data.length === 0) {
    return true;
  }
  if (labels.length !== data.length) {
    return true;
  }
  if (!data.some((v) => typeof v === 'number' && v > 0)) {
    return true;
  }
  return false;
}

const DoughnutChart = ({
  labels,
  datasets,
  colors,
  height = 168,
  className,
  onRendered,
  disableAnimation = false,
}: DoughnutChartProps) => {
  const onRenderedRef = useRef(onRendered);
  onRenderedRef.current = onRendered;

  const primaryDataset = datasets[0];
  const rawData = useMemo(() => primaryDataset?.data ?? [], [primaryDataset]);

  const showPlaceholder = useMemo(
    () => shouldShowDoughnutPlaceholder(labels, rawData),
    [labels, rawData],
  );

  const chartData = useMemo<ChartData<'doughnut'>>(() => {
    const data = rawData;
    const sliceColors = data.map((_, index) =>
      resolveDatasetColor(index, undefined, colors),
    );
    const chartDatasets = [
      {
        label: primaryDataset?.label,
        data,
        backgroundColor: sliceColors,
        borderColor: 'transparent',
        borderWidth: 0,
        unit: primaryDataset?.unit,
      },
    ];
    return { labels, datasets: chartDatasets };
  }, [labels, colors, primaryDataset?.label, primaryDataset?.unit, rawData]);

  const options = useMemo<ChartOptions<'doughnut'>>(
    () => ({
      ...getDoughnutChartOptions(),
      ...(disableAnimation ? { animation: false as const } : {}),
    }),
    [disableAnimation],
  );

  const doughnutPlugins = useMemo((): Plugin<'doughnut'>[] => {
    if (!onRendered || showPlaceholder) {
      return [];
    }

    return [
      createChartRenderCompletePlugin<'doughnut'>(() => {
        onRenderedRef.current?.();
      }),
    ];
  }, [onRendered, showPlaceholder]);

  const heightStyle = typeof height === 'number' ? `${height}px` : height;

  if (showPlaceholder) {
    return (
      <div
        className={clsx('tw-w-full', className)}
        style={{ height: heightStyle }}
        data-cy="analytics-chart-doughnut"
      >
        <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
          <div
            className="tw-box-border tw-aspect-square tw-h-[min(200px,80%)] tw-max-h-full tw-w-auto tw-max-w-[200px] tw-rounded-full tw-border-[8px]"
            style={{ borderColor: CHART_COLORS.mapBase }}
            aria-hidden
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx('tw-w-full', className)}
      style={{ height: heightStyle }}
      data-cy="analytics-chart-doughnut"
    >
      <Doughnut data={chartData} options={options} plugins={doughnutPlugins} />
    </div>
  );
};

export default DoughnutChart;
